import {
  dateFromKey,
  instantTimeOf,
  japaneseHolidayNameOf,
  multiDayEndDateKey,
  WEEKDAYS_JA,
  type EventOccurrence,
  type ScheduleEvent,
} from "@couples/shared";

import { contrastTextColor, withAlpha } from "../lib/color";
import { laneOf } from "../lib/schedule/format";
import type { EventCategory } from "../lib/schedule/categories";

/** 「近日の予定」の日付表示。日を跨ぐ予定は「8月13日〜8月15日(土)」のように範囲で出す */
function eventDateLabel(event: ScheduleEvent, occurrenceDate: string): string {
  const start = dateFromKey(occurrenceDate);
  const endKey = multiDayEndDateKey(event);
  if (endKey === null) {
    return `${start.getMonth() + 1}月${start.getDate()}日(${WEEKDAYS_JA[start.getDay()]})`;
  }
  const end = dateFromKey(endKey);
  return `${start.getMonth() + 1}月${start.getDate()}日〜${end.getMonth() + 1}月${end.getDate()}日(${WEEKDAYS_JA[end.getDay()]})`;
}

/** 「近日の予定」の日付ごとの見出し。月間カレンダーと同じ曜日配色(日曜・祝日は赤、土曜は青)を使う */
function dayHeaderLabel(dateKey: string): {
  text: string;
  isSunOrHoliday: boolean;
  isSaturday: boolean;
} {
  const date = dateFromKey(dateKey);
  const holidayName = japaneseHolidayNameOf(date);
  return {
    text: `${date.getMonth() + 1}月${date.getDate()}日(${WEEKDAYS_JA[date.getDay()]})`,
    isSunOrHoliday: date.getDay() === 0 || holidayName !== null,
    isSaturday: date.getDay() === 6 && holidayName === null,
  };
}

interface Props {
  categories: EventCategory[] | undefined;
  upcomingCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  groups: { date: string; items: EventOccurrence[] }[];
  todayKey: string;
  selfId: string | undefined;
  selfName: string;
  partnerName: string;
  selfColor: string;
  partnerColor: string;
  bothColor: string;
  onOpenOccurrence: (occurrence: EventOccurrence) => void;
}

/** カテゴリ絞り込みチップ + 日付ごとにまとめた近日の予定リスト */
export default function UpcomingEventsList({
  categories,
  upcomingCategoryId,
  onCategoryChange,
  groups,
  todayKey,
  selfId,
  selfName,
  partnerName,
  selfColor,
  partnerColor,
  bothColor,
  onOpenOccurrence,
}: Props) {
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold tracking-[0.08em] text-[var(--color-sub)] uppercase">
        近日の予定
      </h3>

      {categories !== undefined && categories.length > 0 && (
        <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => {
              onCategoryChange(null);
            }}
            className={`shrink-0 rounded-full px-3 py-1 text-[11px] ${
              upcomingCategoryId === null
                ? "bg-[var(--color-teal)] text-white"
                : "border border-[var(--color-line)] text-[var(--color-sub)]"
            }`}
          >
            すべて
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => {
                onCategoryChange(category.id);
              }}
              className={`shrink-0 rounded-full px-3 py-1 text-[11px] ${
                upcomingCategoryId === category.id
                  ? "bg-[var(--color-teal)] text-white"
                  : "border border-[var(--color-line)] text-[var(--color-sub)]"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      {groups.length === 0 && (
        <p className="text-[12px] text-[var(--color-sub)]">近日の予定はありません。</p>
      )}
      {groups.map((group) => {
        const header = dayHeaderLabel(group.date);
        return (
          <div key={group.date} className="mb-3 last:mb-0">
            <div className="mb-1.5 flex items-center gap-2">
              {group.date === todayKey && (
                <span className="shrink-0 rounded-full bg-[var(--color-teal)] px-1.5 py-0.5 text-[9.5px] font-semibold text-white">
                  今日
                </span>
              )}
              <span
                className={`shrink-0 text-[12.5px] font-semibold [font-variant-numeric:tabular-nums] ${
                  header.isSunOrHoliday
                    ? "text-[var(--color-holiday)]"
                    : header.isSaturday
                      ? "text-[var(--color-saturday)]"
                      : ""
                }`}
              >
                {header.text}
              </span>
              <span className="h-px flex-1 bg-[var(--color-line)]" />
              <span className="shrink-0 text-[10.5px] text-[var(--color-sub)]">
                {group.items.length}件
              </span>
            </div>

            {group.items.map((occurrence) => {
              const { event, date } = occurrence;
              const lane = laneOf(event.assignedTo, selfId);
              const isMultiDay = multiDayEndDateKey(event) !== null;
              const laneLabel = lane === "both" ? "2人" : lane === "you" ? selfName : partnerName;
              const laneColor =
                lane === "both" ? bothColor : lane === "you" ? selfColor : partnerColor;
              const laneBadgeStyle =
                lane === "both"
                  ? { backgroundColor: laneColor, color: contrastTextColor(laneColor) }
                  : { backgroundColor: withAlpha(laneColor, "1a"), color: laneColor };
              const timeLabel = event.allDay
                ? "終日"
                : isMultiDay
                  ? `${instantTimeOf(event.startsAt)}〜`
                  : event.endsAt !== null
                    ? `${instantTimeOf(event.startsAt)}〜${instantTimeOf(event.endsAt)}`
                    : instantTimeOf(event.startsAt);
              const category = categories?.find((c) => c.id === event.categoryId) ?? null;
              return (
                <button
                  key={`${event.id}-${date}`}
                  type="button"
                  onClick={() => {
                    onOpenOccurrence(occurrence);
                  }}
                  className="mb-1.5 flex w-full items-stretch gap-2.5 overflow-hidden rounded-xl border border-[var(--color-line)] bg-white text-left"
                >
                  <span
                    className="w-1 shrink-0"
                    style={{ backgroundColor: laneColor }}
                    aria-hidden
                  />
                  <span className="flex flex-1 items-center gap-2.5 py-2.5 pr-3">
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate text-[12.5px] font-medium">{event.title}</span>
                        {category !== null && (
                          <span className="shrink-0 rounded-full bg-[var(--color-teal)]/10 px-1.5 py-0.5 text-[9.5px] font-medium whitespace-nowrap text-[var(--color-teal)]">
                            {category.name}
                          </span>
                        )}
                      </span>
                      <span className="block text-[10.5px] text-[var(--color-sub)]">
                        {isMultiDay ? `${eventDateLabel(event, date)} ` : ""}
                        {timeLabel}
                      </span>
                      {event.memo !== null && event.memo.trim() !== "" && (
                        <span className="mt-0.5 block truncate text-[10.5px] text-[var(--color-sub)]">
                          {event.memo}
                        </span>
                      )}
                    </span>
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                      style={laneBadgeStyle}
                    >
                      {laneLabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
